'use client';

import { useEffect, useState, useMemo } from 'react';
import SpiderChart from './SpiderChart'; // Assuming SpiderChart can accept answers directly
import { useRouter } from 'next/navigation';
// Import dimensions from benchmarks.ts
import { dimensions, industryAverageData } from '../data/benchmarks';
// Import the new component
import ResultListItem from './ResultListItem';
import { generatePDFReport } from './PDFReport';
import ChartGenerator from './ChartGenerator';

interface ResultDisplayProps {
  initialAnswers: number[];
  initialTimes?: number[];
  // Remove dimensions from props if always using the imported one
  // dimensions: string[];
}

// Type the dimensions explicitly (or infer from import if TS setup allows)
const typedDimensions: (string | string[])[] = dimensions;

// Remove local definitions
// const industryAverageData = [73, 61, 46, 51, 46, 52, 48];

// Pass initialAnswers directly, use imported dimensions/industryAverageData
export default function ResultDisplay({ initialAnswers, initialTimes: propInitialTimes = [] }: ResultDisplayProps) {
  const router = useRouter();
  const [resultId, setResultId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(true);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '' });
  const [isUpdatingContact, setIsUpdatingContact] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const answers = initialAnswers;
  const scores = useMemo(() => initialAnswers.map((answer) => (answer + 1) * 25), [initialAnswers]);
  const totalScore = useMemo(() => scores.reduce((sum, score) => sum + score, 0), [scores]);
  const overallRating = useMemo(() => (scores.length > 0 ? Math.round(totalScore / scores.length) : 0), [scores, totalScore]);
  const [chartImageBase64, setChartImageBase64] = useState<string | null>(null);

  const initialTimes = useMemo(() => {
    if (propInitialTimes && propInitialTimes.length > 0) {
      return propInitialTimes;
    }
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('assessmentTimes');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) return parsed;
        } catch {}
      }
    }
    return [];
  }, [propInitialTimes]);

  // Automatically submit results when component loads
  useEffect(() => {
    console.log('[results] mount', {
      answersLen: answers.length,
      scores,
      initialTimes,
      totalScore,
      overallRating,
    });

    const submitResults = async () => {
      console.log('[results] submitting to /api/submit-results ...');
      try {
        setIsSubmitting(true);
        setSubmitError(null);

        let completionTime = 0;
        const start = sessionStorage.getItem('assessmentStartTime');
        if (start) {
          completionTime = Math.floor((Date.now() - parseInt(start, 10)) / 1000);
        }

        const payload = {
          score_1: scores[0], score_2: scores[1], score_3: scores[2],
          score_4: scores[3], score_5: scores[4], score_6: scores[5], score_7: scores[6],
          q1_time: initialTimes[0] ?? null, q2_time: initialTimes[1] ?? null, q3_time: initialTimes[2] ?? null,
          q4_time: initialTimes[3] ?? null, q5_time: initialTimes[4] ?? null, q6_time: initialTimes[5] ?? null,
          q7_time: initialTimes[6] ?? null,
          total_score: totalScore, average_score: overallRating,
          completion_time: completionTime || 0,
          device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
          browser: navigator.userAgent
        };
        console.log('[results] payload', payload);

        const response = await fetch('/api/submit-results', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        console.log('[results] response status', response.status);
        if (!response.ok) throw new Error('Failed to submit results');
        
        const data = (await response.json()) as { success?: boolean; resultId?: number; error?: string };
        console.log('[results] response json', data);
        if (!data.success || !data.resultId) throw new Error(data.error || 'Failed to process submission');

        setResultId(data.resultId);
        sessionStorage.setItem('resultId', data.resultId.toString());
        sessionStorage.removeItem('assessmentStartTime');
        console.log('[results] stored new id', data.resultId);

      } catch (error) {
        console.error('Error submitting results:', error);
        setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setIsSubmitting(false);
      }
    };

    submitResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // This effect now ONLY handles resetting the contact form's visual state
    setShowContactForm(true);
    setUpdateSuccess(false);
    setContactInfo({ name: '', email: '' });
  }, [initialAnswers]);

  const handleRestart = () => {
    try {
      sessionStorage.removeItem('assessmentStep');
      sessionStorage.removeItem('assessmentCurrentQuestion');
      sessionStorage.removeItem('assessmentAnswers');
      sessionStorage.removeItem('assessmentCompleted');
      sessionStorage.removeItem('hasSubmitted');
      sessionStorage.removeItem('assessmentStartTime');
    } catch (error) {
      console.error('Error clearing session storage:', error);
    }

    // Navigate to the benchmark page to start over
    router.push('/benchmark');
  };

  const handleBack = () => {
    try {
      // Resume the assessment at question index 6 (Question 7)
      sessionStorage.setItem('assessmentStep', 'assessment');
      sessionStorage.setItem('assessmentCurrentQuestion', '6');
      sessionStorage.removeItem('assessmentCompleted');
    } catch {}
    router.push('/benchmark');
  };

  // Calculate strengths and improvements once
  const strengths = typedDimensions
    .map((dim, idx) => ({
      dimension: dim,
      index: idx,
      score: scores[idx],
      avg: industryAverageData[idx],
    }))
    .filter((item) => item.score > item.avg);

  const improvements = typedDimensions
    .map((dim, idx) => ({
      dimension: dim,
      index: idx,
      score: scores[idx],
      avg: industryAverageData[idx],
    }))
    .filter((item) => item.score <= item.avg);

  const handleDownloadPDF = async () => {
    try {
      await generatePDFReport(chartImageBase64 || '', overallRating, strengths, improvements, false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  const validateEmail = (email: string) => {
    // Simple email regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setContactInfo(prev => ({ ...prev, [id]: value }));
    if (id === 'email') {
      setEmailValid(value === '' || validateEmail(value));
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (contactInfo.email && !validateEmail(contactInfo.email)) {
      setEmailValid(false);
      return;
    }
    try {
      setIsUpdatingContact(true);
      setUpdateError(null);

      // If we have a resultId, try to update the database record
      if (resultId) {
        const response = await fetch('/api/submit-results/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resultId: resultId,
            name: contactInfo.name,
            email: contactInfo.email,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update contact information.');
        }
        const data = (await response.json()) as { success?: boolean; error?: string };
        if (!data.success) {
          throw new Error(data.error || 'Failed to update contact information.');
        }
      }

      // Generate and send PDF report regardless of resultId
      if (contactInfo.email && validateEmail(contactInfo.email)) {
        try {
          const pdfBase64 = await generatePDFReport(chartImageBase64 || '', overallRating, strengths, improvements, true);
          if (pdfBase64) {
            const reportResponse = await fetch('/api/send-report', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              // --- TEMPORARY TEST CODE ---
              // Send a minimal payload to test the theory that the request size is the issue.
              body: JSON.stringify({
                name: contactInfo.name || 'Test User',
                email: contactInfo.email,
                pdfBase64: 'HI', // Use a tiny dummy string instead of the real PDF data
              }),
            });

            if (!reportResponse.ok) {
              const errorData = (await reportResponse.json()) as { message?: string };
              throw new Error(errorData.message || 'Failed to send the report email.');
            }
          }
        } catch (reportError) {
          console.error('Failed to generate or send PDF report:', reportError);
          // Now we will show this error to the user
          throw reportError;
        }
      }

      setUpdateSuccess(true);
      setTimeout(() => {
        setShowContactForm(false);
      }, 2000); // Hide after 2 seconds
    } catch (error) {
      console.error('Error updating contact:', error);
      setUpdateError(error instanceof Error ? error.message : 'An unexpected error occurred.');
    } finally {
      setIsUpdatingContact(false);
    }
  };

  return (
    <div className="text-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto rounded-lg border border-gray-800 bg-gray-850 p-4 sm:p-6">
        {/* Top bar removed per request */}

        {/* Add ChartGenerator at the top level */}
        <ChartGenerator answers={answers} onChartGenerated={setChartImageBase64} />
        
        {/* Show submission status */}
        {isSubmitting && (
          <div className="fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded shadow-lg">
            Saving your results...
          </div>
        )}
        
        {submitError && (
          <div className="fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded shadow-lg">
            {submitError}
          </div>
        )}

        {/* Contact Form Modal */}
        {showContactForm && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-gray-800 text-gray-100 rounded-lg p-6 sm:p-8 max-w-md w-full mx-4 border border-gray-700 shadow-2xl">
              {updateSuccess ? (
                <>
                  <h2 className="text-2xl font-bold mb-3 text-center text-[#59B9C1]">Thank You!</h2>
                  <p className="text-gray-200 text-center">Your report is on its way. This window will close shortly.</p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-3 text-[#59B9C1] text-center">Would you like to receive your results?</h2>
                  <p className="text-gray-200 mb-2 text-center">
                    Enter your name and email to receive a copy of your results. This is completely optional.
                  </p>
                  <p className="text-xs text-gray-400 mb-4 text-center">
                    By submitting, you agree to our{' '}
                    <a href="https://www.datajukebox.net/data-privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#59B9C1]">Privacy Policy</a>.
                  </p>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300">Name</label>
                      <input
                        type="text"
                        id="name"
                        value={contactInfo.name}
                        onChange={handleContactChange}
                        className="mt-1 block w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-[#59B9C1] focus:border-[#59B9C1] sm:text-sm text-gray-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300">Email</label>
                      <input
                        type="email"
                        id="email"
                        value={contactInfo.email}
                        onChange={handleContactChange}
                        className={`mt-1 block w-full px-3 py-2 bg-gray-800 border ${emailValid ? 'border-gray-700' : 'border-red-500'} rounded-md shadow-sm focus:outline-none focus:ring-[#59B9C1] focus:border-[#59B9C1] sm:text-sm text-gray-100`}
                      />
                      {!emailValid && <p className="mt-2 text-sm text-red-400">Please enter a valid email address.</p>}
                    </div>

                    {updateError && (
                      <div className="text-red-400 text-sm">{updateError}</div>
                    )}

                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        type="button"
                        onClick={() => setShowContactForm(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-700 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 focus:ring-offset-gray-900"
                      >
                        Skip
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdatingContact || (!!contactInfo.email && !emailValid)}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#59B9C1] rounded-md hover:bg-[#4aa7ae] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#59B9C1] focus:ring-offset-gray-900 disabled:opacity-50"
                      >
                        {isUpdatingContact ? 'Saving...' : 'Save & Continue'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Main Container */}
        <div className="w-full mx-auto my-4 max-w-3xl rounded-xl bg-gray-800 p-6 sm:p-8 border border-gray-700 flex flex-col items-center">
          {/* Title */}
          <h3 className="text-3xl sm:text-4xl font-bold text-[#59B9C1] mb-4 text-center">Your Results</h3>

          {/* Graph Section */}
          <div className="relative w-full flex justify-center mb-6">
            <div className="w-[600px] h-[600px] max-w-full">
              <SpiderChart answers={answers} width={600} height={600} />
            </div>
          </div>

          {/* Score Section */}
          <div className="text-center mt-2 w-full">
            <h4 className="text-xl sm:text-2xl font-semibold text-gray-100 mb-2">Your Overall Score</h4>
            <p className="text-4xl sm:text-5xl font-bold text-[#59B9C1] mb-2">{overallRating}%</p>
            <p className="text-gray-300 max-w-2xl mx-auto">
              How to increase your score to 100%? Please refer to your analysis and linked case studies below or contact us.
            </p>
          </div>
        </div>

        {/* Analysis Container */}
        <div className="mx-auto my-4 max-w-3xl rounded-xl bg-gray-800 p-6 sm:p-8 border border-gray-700">
          <h3 className="mb-4 text-2xl font-semibold text-gray-100">Analysis of Your Results</h3>

          {/* Key Strengths */}
          <div className="mb-6">
            <h4 className="mb-3 text-xl font-semibold text-[#59B9C1]">Key Strengths</h4>
            <ul className="m-0 list-none p-0">
              {strengths.length > 0 ? (
                strengths.map((item) => {
                  const dimensionName = Array.isArray(item.dimension)
                    ? item.dimension.join(' ')
                    : item.dimension;
                  return (
                    <ResultListItem
                      key={dimensionName}
                      dimensionName={dimensionName}
                      score={item.score}
                      industryAvg={item.avg}
                      type="strength"
                    />
                  );
                })
              ) : (
                <li className="text-gray-300">No specific strengths identified compared to industry average. Focus on areas for improvement below.</li>
              )}
            </ul>
          </div>

          {/* Areas for Improvement */}
          <div className="mb-6">
            <h4 className="mb-3 text-xl font-semibold text-[#F85640]">Areas for Improvement</h4>
            <ul className="m-0 list-none p-0">
              {improvements.length > 0 ? (
                improvements.map((item) => {
                  const dimensionName = Array.isArray(item.dimension)
                    ? item.dimension.join(' ')
                    : item.dimension;
                  return (
                    <ResultListItem
                      key={dimensionName}
                      dimensionName={dimensionName}
                      score={item.score}
                      industryAvg={item.avg}
                      type="improvement"
                    />
                  );
                })
              ) : (
                <li className="text-gray-300">Excellent results! All areas are above the industry average.</li>
              )}
            </ul>
          </div>

          <p className="mt-6 pt-4 border-t border-gray-700 text-center text-sm text-gray-300 italic">
            Please note: The analysis above compares your scores against the industry average. Leading
            pharmaceutical companies often aim towards best practice (100% in all areas) to maximize
            the benefits of HCP personalisation.
          </p>

          {/* Bottom Buttons */}
          <div className="mt-6 flex flex-col items-center justify-center gap-4 text-center md:flex-row md:flex-wrap md:items-center">
            <div className="flex flex-col w-full gap-4 md:flex-row md:items-center md:justify-center justify-center">
              <button
                className="w-full max-w-xs rounded-lg bg-[#59B9C1] px-6 py-3 font-semibold text-white transition hover:bg-[#4aa7ae] md:w-auto"
                onClick={handleRestart}
              >
                Take New Assessment
              </button>

              <button
                className="w-full max-w-xs rounded-lg bg-[#59B9C1] px-6 py-3 font-semibold text-white transition hover:bg-[#4aa7ae] md:w-auto"
                onClick={handleDownloadPDF}
              >
                Download PDF Report
              </button>
            </div>

            <div className="flex flex-col w-full gap-4 md:flex-row md:items-center md:justify-center justify-center mt-2">
              <button
                className="w-full max-w-xs rounded-lg bg-gray-700 px-6 py-3 font-semibold text-white transition hover:bg-gray-600 md:w-auto"
                onClick={handleBack}
              >
                Go Back
              </button>
              <a
                className="inline-flex w-full max-w-xs items-center justify-center rounded-lg border border-gray-600 bg-transparent px-6 py-3 text-center text-gray-200 transition hover:bg-gray-700/40 md:w-auto"
                onClick={() => router.push('/case-studies')}
              >
                <span className="font-semibold">Learn More</span>
              </a>
              <button
                className="inline-flex w-full max-w-xs items-center justify-center rounded-lg border border-[#59B9C1] bg-transparent px-6 py-3 text-center font-semibold text-[#59B9C1] transition hover:bg-[#59B9C1]/10 md:w-auto"
                onClick={() => router.push('/contact')}
              >
                <span className="font-semibold">Contact</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
